import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BookingService from "../../services/api/booking.service";
import AuthService from "../../services/api/auth.service";
import { bookingOpen } from "../../actions/booking.action";
import ModalConfirmationResa from "./ModalConfirmationResa";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';

interface FormData {
  name: string;
  phone: string;
  email: string;
  nbPerson: number;
  date: string;
  hour: string;
}

const BookingForm: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch: any = useDispatch();
  const openResa = useSelector((state: any) => state.bookingReducer);
  const [token, setToken] = useState<string>("");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    email: "",
    nbPerson: 1,
    date: "",
    hour: "",
  });
  const [availableHours, setAvailableHours] = useState<string[]>([]);
  const [maxPeople, setMaxPeople] = useState<number>(6);
  const [unavailableDates, setUnavailableDates] = useState<string[]>([]);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    dispatch(bookingOpen(isOpen));
  };

  useEffect(() => {
    const userToken = AuthService.getUserConnected();
    if (userToken) {
      setToken(userToken);
    }
  }, []);

  function transformDateToFrench(dateString: string): string {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string");
    }
    const formattedDate = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
    return formattedDate;
  }

  useEffect(() => {
    const fetchUnavailableDates = async () => {
      try {
        const selectedDaysResponse = await BookingService.getAllBlockedDay(token);
        const blockedDays = selectedDaysResponse.data.map((day) => day.date);
        setUnavailableDates(blockedDays);
        console.log(blockedDays);
      } catch (error) {
        console.error("Erreur lors de la récupération des jours bloqués :", error);
      }
    };
    fetchUnavailableDates();
  }, [token]);

  useEffect(() => {
    if (!formData.date) return;
    const fetchBookingsByDate = async () => {
      try {
        const formattedDate = new Date(formData.date).toISOString().split("T")[0];
        const response = await BookingService.getAllBookingByDates(formattedDate, token);
        const fetchedBookings = response.data;
        const hourTotals: { [key: string]: number } = {};
        fetchedBookings.forEach((booking) => {
          const hour = booking.date.split("T")[1].slice(0, 5);
          hourTotals[hour] = (hourTotals[hour] || 0) + booking.nbPerson;
        });
        const filteredHours = availableTimes.filter((hour) => (hourTotals[hour] || 0) < 6);
        setAvailableHours(filteredHours);
      } catch (error) {
        console.error("Error fetching bookings:", error);
      }
    };
    fetchBookingsByDate();
  }, [formData.date, token]);

  const fetchAvailability = async () => {
    try {
      const formattedDate = new Date(formData.date).toISOString().split("T")[0];
      const response = await BookingService.getAllBookingByDates(formattedDate, token);
      const fetchedBookings = response.data;
      const hourTotals: { [key: string]: number } = {};
      fetchedBookings.forEach((booking) => {
        const hour = booking.date.split("T")[1].slice(0, 5);
        hourTotals[hour] = (hourTotals[hour] || 0) + booking.nbPerson;
      });
      const availableSpots = 6 - (hourTotals[formData.hour] || 0);
      setMaxPeople(availableSpots);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  useEffect(() => {
    if (!formData.hour) return;
    fetchAvailability();
  }, [formData.hour, token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const formattedDate = format(date, 'yyyy-MM-dd');
      setFormData({ ...formData, date: formattedDate });
    } else {
      setFormData({ ...formData, date: "" });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const reservationData = {
        ...formData,
        date: `${formData.date}T${formData.hour}:00`,
      };
      await BookingService.post(reservationData);
      setShowModal(true);
      setFormData({ name: "", phone: "", email: "", nbPerson: 1, date: "", hour: "" });
      setIsOpen(false);
    } catch (error) {
      console.error("Erreur lors de la réservation:", error);
      alert("Une erreur est survenue lors de la réservation.");
    }
  };

  const getMinDate = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1);
    return today;
  };

  const availableTimes = [
    "12:00", "12:30", "13:00", "13:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30"
  ];

  const isDateUnavailable = (date: Date) => {
    const formattedDate = format(date, 'yyyy-MM-dd');
    return unavailableDates.includes(formattedDate);
  };

  const handleDatePickerOpen = () => {
    fetchAvailability()
    setIsDatePickerOpen(true);
  };

  const handleDatePickerClose = () => {
    setIsDatePickerOpen(false);
  };

  return (
    <>
      <div className={`reservation-form ${openResa ? "open" : "closed"}`}>
        <div className="reservation-header" onClick={handleToggle}>
          <span>{openResa ? "✖" : "Réserver une table"}</span>
        </div>
        {openResa && (
          <form onSubmit={handleSubmit} className={`${isDatePickerOpen ? 'openCalendar' : 'closedCalendar'}`}>
            <label htmlFor="name">Nom</label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
            <label htmlFor="phone">Téléphone</label>
            <input type="phone" id="phone" name="phone" value={formData.phone} onChange={handleChange} required />
            <label htmlFor="email">Email</label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
            <label htmlFor="date">Date</label>
            <div className="date-picker-container">
              <DatePicker
                selected={formData.date ? new Date(formData.date) : null}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                minDate={getMinDate()}
                onCalendarOpen={handleDatePickerOpen}
                onCalendarClose={handleDatePickerClose}
                filterDate={(date) => !isDateUnavailable(date)}
                customInput={ <div className="input">
                  <input
                    type="text"
                    id="date"
                    name="date"
                    style={{color:"#000",cursor:"pointer"}}
                    value={formData.date ? format(formData.date, 'dd-MM-yyyy') : ''}
                    readOnly
                    required
                    placeholder="Select a date"
                  />
                  <div style={{position:"relative",right: "-236px", top: "-24px"}}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                  </div>
                </div>}
              />

            </div>


            {formData.date && (
              <>
                <label htmlFor="hour">Heure</label>
                <select id="hour" style={{cursor:"pointer"}} name="hour" value={formData.hour} onChange={handleChange} required>
                  <option value="" disabled>Sélectionnez une heure</option>
                  {availableHours.map((hour) => (
                    <option key={hour} value={hour}>{hour}</option>
                  ))}
                </select>
              </>
            )}
            {formData.hour && (
              <>
                <label htmlFor="nbPerson">Nombre de personnes</label>
                <select id="nbPerson" style={{cursor:"pointer"}} name="nbPerson" value={formData.nbPerson} onChange={handleChange} required>
                  {[...Array(maxPeople)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </>
            )}
            {formData.nbPerson && <button type="submit">Réserver</button>}
          </form>
        )}
      </div>
      {showModal && <ModalConfirmationResa message="Votre réservation est confirmée ! Un e-mail de confirmation vous sera envoyé sous peu." onClose={() => setShowModal(false)} />}
    </>
  );
};

export default BookingForm;